// frontend/src/components/admin/modals/CreateTournamentModal.jsx
import React, { useState, useEffect  } from 'react';
import axios from '../../../config/axios';
import { Calendar, Clock, AlertCircle } from 'lucide-react';

const DAYS_OF_WEEK = [
    { id: 0, name: 'Domingo' },
    { id: 1, name: 'Lunes' },
    { id: 2, name: 'Martes' },
    { id: 3, name: 'Miércoles' },
    { id: 4, name: 'Jueves' },
    { id: 5, name: 'Viernes' },
    { id: 6, name: 'Sábado' }
];

const CreateTournamentModal = ({ onClose, onSuccess }) => {
    const [isRecurring, setIsRecurring] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [games, setGames] = useState([]);

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const response = await axios.get('/games');
                // Obtener solo juegos activos
                const activeGames = [
                    ...response.data.data.featured,
                    ...response.data.data.active
                ];
                setGames(activeGames);
                // Establecer el primer juego como valor por defecto
                if (activeGames.length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        gameType: activeGames[0].slug
                    }));
                }
            } catch (err) {
                console.error('Error al cargar juegos:', err);
                setError('Error al cargar los juegos disponibles');
            }
        };

        fetchGames();
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        gameType: 'truco',
        entryFee: 5,
        minPlayers: 2,
        maxPlayers: 8,
        teamSize: 1,
        startDate: '',
        startTime: '',
        daysOfWeek: [],
        recurringTime: ''
    });

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;

        if (name === 'teamSize') {
            const parsedValue = parseInt(value, 10);
            const minPlayers = parsedValue * 2;
            const maxPlayers = parsedValue * 8;

            setFormData(prev => ({
                ...prev,
                teamSize: parsedValue,
                minPlayers,
                maxPlayers
            }));
            return;
        }

        // Convertir valores numéricos
        if (type === 'number') {
            setFormData(prev => ({
                ...prev,
                [name]: value === '' ? '' : parseInt(value, 10)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleDayToggle = (dayId) => {
        setFormData(prev => ({
            ...prev,
            daysOfWeek: prev.daysOfWeek.includes(dayId)
                ? prev.daysOfWeek.filter(d => d !== dayId)
                : [...prev.daysOfWeek, dayId].sort()
        }));
    };

    const validateForm = () => {
        if (!formData.name) return 'El nombre es requerido';
        if (formData.entryFee < 1) return 'La entrada debe ser al menos 1 ticket';
        if (formData.minPlayers < formData.teamSize * 2) return 'Mínimo 2 equipos completos';
        if (formData.maxPlayers < formData.minPlayers) return 'El máximo debe ser mayor al mínimo';
        if (formData.minPlayers % formData.teamSize !== 0) return 'El mínimo debe ser múltiplo del tamaño del equipo';
        if (formData.maxPlayers % formData.teamSize !== 0) return 'El máximo debe ser múltiplo del tamaño del equipo';

        if (isRecurring) {
            if (formData.daysOfWeek.length === 0) return 'Selecciona al menos un día';
            if (!formData.recurringTime) return 'Selecciona la hora del torneo';
        } else {
            if (!formData.startDate) return 'Selecciona la fecha de inicio';
            if (!formData.startTime) return 'Selecciona la hora de inicio';
        }

        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError('');

        try {
            let startTime;
            if (!isRecurring) {
                startTime = new Date(`${formData.startDate}T${formData.startTime}`).toISOString();
            }

            const dataToSend = {
                ...formData,
                entryFee: parseInt(formData.entryFee, 10),
                minPlayers: parseInt(formData.minPlayers, 10),
                maxPlayers: parseInt(formData.maxPlayers, 10),
                isRecurring,
                startTime,
                recurringTime: isRecurring ? formData.recurringTime : undefined,
                daysOfWeek: isRecurring ? formData.daysOfWeek : undefined
            };

            const response = await axios.post('/admin/tournaments', dataToSend);

            if (response.data.success) {
                onSuccess();
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al crear el torneo');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4">
                <h2 className="text-xl font-bold text-primary mb-4">Crear Nuevo Torneo</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Tipo de torneo */}
                    <div className="flex space-x-4">
                        <button
                            type="button"
                            onClick={() => setIsRecurring(false)}
                            className={`flex-1 p-4 rounded-lg border-2 ${!isRecurring ? 'border-primary bg-primary-light/10' : 'border-gray-200'}`}
                        >
                            <Calendar className="w-6 h-6 mx-auto mb-2" />
                            <p className="text-sm font-medium">Fecha Específica</p>
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsRecurring(true)}
                            className={`flex-1 p-4 rounded-lg border-2 ${isRecurring ? 'border-primary bg-primary-light/10' : 'border-gray-200'}`}
                        >
                            <Clock className="w-6 h-6 mx-auto mb-2" />
                            <p className="text-sm font-medium">Torneo Recurrente</p>
                        </button>
                    </div>

                    {/* Información básica */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nombre del Torneo
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border rounded-md"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Juego
                            </label>
                            <select
                                name="gameType"
                                value={formData.gameType}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border rounded-md"
                                required
                            >
                                {games.map(game => (
                                    <option key={game.slug} value={game.slug}>
                                        {game.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Formato
                            </label>
                            <select
                                name="teamSize"
                                value={formData.teamSize}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border rounded-md"
                                required
                            >
                                <option value={1}>1 vs 1</option>
                                <option value={2}>2 vs 2</option>
                                <option value={3}>3 vs 3</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Entrada (tickets)
                            </label>
                            <input
                                type="number"
                                name="entryFee"
                                value={formData.entryFee}
                                onChange={handleInputChange}
                                min="1"
                                className="w-full px-3 py-2 border rounded-md"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Jugadores Mínimos
                            </label>
                            <input
                                type="number"
                                name="minPlayers"
                                value={formData.minPlayers}
                                onChange={handleInputChange}
                                min={formData.teamSize * 2}
                                className="w-full px-3 py-2 border rounded-md"
                                required
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Múltiplo de {formData.teamSize} para completar equipos.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Jugadores Máximos
                            </label>
                            <input
                                type="number"
                                name="maxPlayers"
                                value={formData.maxPlayers}
                                onChange={handleInputChange}
                                min={formData.teamSize * 2}
                                className="w-full px-3 py-2 border rounded-md"
                                required
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Múltiplo de {formData.teamSize} para completar equipos.
                            </p>
                        </div>
                    </div>

                    {/* Programación */}
                    {isRecurring ? (
                        <div className="space-y-4">
                            <label className="block text-sm font-medium text-gray-700">
                                Días de la Semana
                            </label>
                            <div className="grid grid-cols-7 gap-2">
                                {DAYS_OF_WEEK.map(day => (
                                    <button
                                        key={day.id}
                                        type="button"
                                        onClick={() => handleDayToggle(day.id)}
                                        className={`p-2 text-sm rounded-lg text-center transition-colors
                                            ${formData.daysOfWeek.includes(day.id)
                                            ? 'bg-primary text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {day.name.slice(0, 3)}
                                    </button>
                                ))}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Hora del Torneo
                                </label>
                                <input
                                    type="time"
                                    name="recurringTime"
                                    value={formData.recurringTime}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                    required={isRecurring}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Fecha
                                </label>
                                <input
                                    type="date"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleInputChange}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="w-full px-3 py-2 border rounded-md"
                                    required={!isRecurring}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Hora
                                </label>
                                <input
                                    type="time"
                                    name="startTime"
                                    value={formData.startTime}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                    required={!isRecurring}
                                />
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 p-4 rounded-md flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                            <p className="text-sm text-red-500">{error}</p>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-light disabled:opacity-50"
                        >
                            {loading ? 'Creando...' : 'Crear Torneo'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateTournamentModal;
